import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputLabel,
    TextField,
    Paper,
    Stack,
    Chip,
    TableRow,
    Radio,
    TableCell,
    Table,
    TableHead,
    Typography,
    Box,
    Tooltip,
    TableBody,
    LinearProgress
} from "@mui/material";
import Grid from '@mui/material/Grid';
import {Outlet, useLocation} from "react-router-dom";
import {useLazyQuery, useMutation} from "@apollo/client";
import {useEffect, useState} from "react";
import moment from "moment-timezone";

import CustomAlert from "./CustomAlert";
import {BOOKING_LESSON, LOOKUP_PARENT_WITH_CHILDREN, QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_AVAILABILITY_FILTERED_BY_TIME} from "../queries/GraphQLQueries";
import {useDialogNavigation} from "../hooks/useDialogNavigation";

export default function MakeBookingDialog() {

    const { state, location } = useLocation();
    const handleClose = useDialogNavigation();
    const routeMemberId = location && location.state ? location.state.memberId : null;
    const [lookupParent, {loading, error, data}] = useLazyQuery(LOOKUP_PARENT_WITH_CHILDREN);
    const [createBookingMutation, { data: createBookingData, loading: bookingResponseLoading, error: bookingError }] = useMutation(BOOKING_LESSON, {
        refetchQueries: [{ query: QUERY_CAN_TEACH_DOMAIN_USERS_AND_THEIR_AVAILABILITY_FILTERED_BY_TIME }, 'GetTeachingUsersAndTheirAvailability']
    });

    const KEY_RETURN = 'Enter';

    const [externalId, setExternalId] = useState(routeMemberId || "");
    const [parent, setParent] =  useState();
    const [lookupEnabled, setLookupEnabled] = useState(false);
    const [isShiftDataLoading, setIsShiftDataLoading] = useState(false);
    const {performer, timeslotTime, serviceTypeDescription, content, lessonId} = state || {};
    const [alertMessage, setAlertMessage] = useState(null);
    const [selectedChildId, setSelectedChildId] = useState('');

    const handleChildSelection = (childId) => setSelectedChildId(childId);

    const handleBook = () => {
        const selectedChild = parent.children.find(child => child.id === selectedChildId);
        setAlertMessage({children: 'Making booking...', severity: 'success'});
        if (selectedChild) {
            createBookingMutation({
                variables: {
                    externalChildId: selectedChild.externalId,
                    lessonId: lessonId
                }
            })
            .then(() => handleClose())
            .catch((e) => setAlertMessage({children: e?.message, severity: 'error'}));
        } else {
            setAlertMessage({children: "Selected child not found.", severity: 'error'});
        }
    }

    const TooltipTableCell = ({params}) => {
        return <Tooltip title={params}>
            <TableCell>{params}</TableCell>
        </Tooltip>;
    }

    useEffect(() => {
        if (routeMemberId){
          lookupMember();
        }
    }, []);

    useEffect(() => {
      setLookupEnabled(externalId !== "");
    }, [externalId, parent]);

    useEffect(() => {
      if (data?.parentInfo) {
        setParent(data.parentInfo);
      }
    }, [data]);

    const handleCloseCustomAlert = () => setAlertMessage(null);

    const handleExternalIdFieldChanged = (e) => setExternalId(e.target.value.trim());

    const lookupMember = () => {
        setIsShiftDataLoading(true);
        lookupParent({variables: {externalId: externalId}})
        .then((result) => {
            setIsShiftDataLoading(false);
            if (!result?.data.parentInfo) {
                setAlertMessage({children: `Member id '${externalId}' could not be found.`, severity: 'warning'});
            } else {
                setParent(result.data.parentInfo);
            }
        })
        .catch((e) => {
            setIsShiftDataLoading(false);
            setAlertMessage({children: "Error fetching member record " + e.message, severity: 'error'});
            console.log("error " + e)
        });
    }

    const handleClearClicked = () => {
      setParent(null);
      setExternalId('');
      setSelectedChildId('');
    }

    return (
        <>
        <Dialog open={true} maxWidth={"md"} fullWidth={true} onClose={handleClose}>
            <DialogTitle>Create Booking</DialogTitle>

            <DialogContent>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <InputLabel id="service-type-label">Service Type</InputLabel>
                        <TextField
                          id="service-type-description"
                          labelId="service-type-label"
                          fullWidth
                          variant="standard"
                          slotProps={{input: {readOnly: true}}}
                          value={serviceTypeDescription}/>
                    </Grid>
                    <Grid size={6}>
                        <InputLabel id="performer-label">Performer</InputLabel>
                        <TextField
                          id="performer-name"
                          labelId="performer-label"
                          fullWidth
                          variant="standard"
                          slotProps={{input: {readOnly: true}}}
                          value={performer}/>
                    </Grid>
                    <Grid size={12}>
                        <InputLabel id="timeslot-label">Timeslot</InputLabel>
                        <TextField
                          id="timeslot-time"
                          labelId="timeslot-label"
                          variant="standard"
                          fullWidth
                          slotProps={{input: {readOnly: true}}}
                          value={moment(timeslotTime).format('dddd MMM Do , h:mm a')}/>
                    </Grid>
                    <Grid size={12}>
                    <InputLabel id="content-label">Content</InputLabel>
                        <Grid style={{maxHeight: '70px', overflowY: 'auto'}}>
                            {content?.map(contentItem =>
                              <Chip key={contentItem.id} label={contentItem.name} style={{margin: '2px', fontSize: '16px'}} slotProps={{input: {readOnly: true}}}/>)}
                        </Grid>
                    </Grid>
                </Grid>

            <Typography variant="body1" sx={{my: 2, paddingLeft: '6px', marginTop: '2rem'}}>Add Child</Typography>
            <Grid size={12}>
                {isShiftDataLoading && <LinearProgress />}

                {!parent
                  && <Paper sx={{p: 2}} elevation={1}>
                      <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
                          <TextField
                              id="externalId"
                              label="External Id / Member Id"
                              variant="standard"
                              value={externalId}
                              onChange={handleExternalIdFieldChanged}
                              onKeyDown={(e) => e.key === KEY_RETURN && lookupMember()}/>
                          <Button variant="contained" disabled={!lookupEnabled || isShiftDataLoading} onClick={lookupMember}>Lookup</Button>
                      </Stack>
                  </Paper>
                }

                {parent
                  && <Box>
                      <Typography variant="body1" sx={{marginBottom: "6px"}}>Member Details</Typography>
                      <Paper elevation={4}>
                          <Stack spacing={2} sx={{p: 2}} alignItems="center" justifyContent="center" direction="row">
                              <TextField id="recordId" label="Id" value={parent?.id || ''} slotProps={{input: {readOnly: true}}}/>
                              <TextField id="externalId" label="Member Id" value={parent?.externalId || ''} slotProps={{input: {readOnly: true}}}/>
                              <TextField id="displayName" label="Display Name" value={parent?.displayName || ''} slotProps={{input: {readOnly: true}}}/>
                              <TextField id="region" label="Region" value={parent?.region || ''} slotProps={{input: {readOnly: true}}}/>
                              <Button variant="contained" onClick={handleClearClicked}>Clear</Button>
                          </Stack>
                      </Paper>

                      <Typography variant="h6" sx={{my: 2}}>Children</Typography>
                      <Paper>
                          <Table size="small">
                              <TableHead>
                                  <TableRow>
                                      <TableCell>Id</TableCell>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Latin Name</TableCell>
                                      <TableCell>Gender</TableCell>
                                      <TableCell>Date of Birth</TableCell>
                                      <TableCell>Select</TableCell>
                                  </TableRow>
                              </TableHead>
                              <TableBody>
                                  {parent?.children
                                    && parent.children.map((child) => (
                                      <TableRow key={child.id}>
                                          <TooltipTableCell params={child.id}/>
                                          <TooltipTableCell params={child.displayName}/>
                                          <TooltipTableCell params={child.displayNameRomanized}/>
                                          <TooltipTableCell params={child.gender}/>
                                          <TooltipTableCell params={child.dateOfBirth}/>
                                          <Radio checked={selectedChildId === child.id}
                                                 onChange={() => handleChildSelection(child.id)}
                                                 value={child.id}
                                                 name="child-radio"
                                                 inputProps={{'aria-label': child.displayName}}
                                                 sx={{marginLeft: '14px', marginTop: '4px'}} />
                                      </TableRow>
                                    ))}
                              </TableBody>
                          </Table>
                      </Paper>
                  </Box>
                }
                <Outlet />
            </Grid>

            <Typography variant="body1" sx={{ mt: 2 }}>
                * Please note that this booking will <strong>not</strong> be charged against the members account or allowance.
            </Typography>

            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="contained">Cancel</Button>
                <Button onClick={handleBook} disabled={!selectedChildId} variant="contained">Book</Button>
            </DialogActions>
        </Dialog>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
        </>
    );
}
